import React, { useState } from 'react';
import { supabase } from '../config/supabase';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  InputAdornment,
  Card,
  CardContent,
  Slider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  IconButton
} from '@mui/material';
import {
  Calculate as CalculateIcon,
  AccountBalance as AccountBalanceIcon,
  Savings as SavingsIcon,
  EventRepeat as EventRepeatIcon,
  ShowChart as ShowChartIcon,
  Payments as PaymentsIcon,
  Percent as PercentIcon,
  AccountBalanceWallet as WalletIcon,
  CreditScore as CreditScoreIcon,
  ReceiptLong as ReceiptLongIcon,
  Description as DescriptionIcon,
  WorkHistory as WorkHistoryIcon,
  ArrowBack as ArrowBackIcon,
  Apps as AppsIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// Calculator list (order must match TabPanel indexes)
const CALCULATORS = [
  { label: 'Simple FD', description: 'Fixed deposit, simple interest', icon: AccountBalanceIcon, color: '#1976d2' },
  { label: 'Compound FD', description: 'Fixed deposit, compounded', icon: SavingsIcon, color: '#2e7d32' },
  { label: 'RD', description: 'Recurring deposit', icon: EventRepeatIcon, color: '#ed6c02' },
  { label: 'SIP', description: 'Systematic investment plan', icon: ShowChartIcon, color: '#9c27b0' },
  { label: 'Monthly Payout', description: 'Regular monthly income', icon: PaymentsIcon, color: '#0288d1' },
  { label: 'Monthly Interest', description: 'Interest earned per month', icon: PercentIcon, color: '#00897b' },
  { label: 'SWP', description: 'Systematic withdrawal plan', icon: WalletIcon, color: '#5e35b1' },
  { label: 'EMI', description: 'Loan EMI & schedule', icon: CreditScoreIcon, color: '#d32f2f' },
  { label: 'Income Tax', description: 'Old vs new regime', icon: ReceiptLongIcon, color: '#c2185b' },
  { label: 'ITR-2', description: 'Tax with capital gains', icon: DescriptionIcon, color: '#455a64' },
  { label: 'Gratuity', description: 'Gratuity on retirement', icon: WorkHistoryIcon, color: '#6d4c41' }
];

// Tab Panel Component
function TabPanel({ children, value, index, sx, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`calculator-tabpanel-${index}`}
      aria-labelledby={`calculator-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 0, sm: 2, md: 3 }, pt: { xs: 1, sm: 2, md: 3 }, ...sx }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount);
};

// Helper function for chart colors
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

// Custom tooltip for pie chart
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <Card sx={{ p: 1 }}>
        <Typography variant="body2">
          {payload[0].name}: {formatCurrency(payload[0].value)}
        </Typography>
      </Card>
    );
  }
  return null;
};

const FinanceCalculator = () => {
  // Add this style object at the top level of the component
  const numberInputStyle = {
    '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
  };

  const [activeTab, setActiveTab] = useState(0);

  // Mobile layout: show icon-block menu first, then the selected calculator
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showMobileMenu, setShowMobileMenu] = useState(true);

  const openCalculator = (index) => {
    setActiveTab(index);
    setShowMobileMenu(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Simple FD Calculator State
  const [simpleFD, setSimpleFD] = useState({
    principal: '',
    rate: '',
    years: '',
    months: '',
    days: '',
    result: null
  });

  // Compound FD Calculator State
  const [compoundFD, setCompoundFD] = useState({
    principal: '',
    rate: '',
    years: '',
    months: '',
    days: '',
    frequency: 1, // 1=yearly, 4=quarterly, 12=monthly
    result: null
  });

  // RD Calculator State
  const [rd, setRD] = useState({
    monthlyInvestment: '',
    rate: '',
    years: '',
    months: '',
    result: null
  });

  // SIP Calculator State
  const [sip, setSIP] = useState({
    monthlyInvestment: '',
    rate: '',
    years: '',
    months: '',
    stepUp: '', // Yearly appraisal percentage
    result: null
  });

  // Monthly Payout Calculator State
  const [monthlyPayout, setMonthlyPayout] = useState({
    principal: '',
    rate: '',
    time: '',
    result: null
  });

  // Monthly Interest Calculator State
  const [monthlyInterest, setMonthlyInterest] = useState({
    principal: '',
    rate: '',
    time: '',
    result: null
  });

  // SWP Calculator State
  const [swp, setSWP] = useState({
    principal: '',
    rate: '',
    time: '',
    increment: '',
    result: null,
    yearlyData: []
  });

  // EMI Calculator State
  const [emi, setEMI] = useState({
    principal: '',
    rate: '',
    years: '',
    months: '',
    result: null,
    breakup: null
  });

  // EMI Pagination State
  const [emiCurrentYear, setEmiCurrentYear] = useState(1);

  // Income Tax Calculator State
  const [incomeTax, setIncomeTax] = useState({
    salary: '',
    otherIncome: '',
    deductions: '', // New regime: allowed deductions only (e.g. employer NPS u/s 80CCD(2))
    regime: 'new', // new or old
    // Old regime inputs
    ageGroup: 'below60', // below60 | senior | superSenior
    hra: '',
    sec80C: '',
    sec80D: '',
    sec80CCD1B: '',
    homeLoanInterest: '',
    otherDeductions: '',
    result: null,
    comparison: null
  });

  // ITR-2 Calculator State
  const [itr2, setITR2] = useState({
    salary: '',
    otherIncome: '',
    deductions: '',
    shortTermGains: '',
    longTermGains: '',
    regime: 'new',
    result: null
  });

  // Gratuity Calculator State
  const [gratuity, setGratuity] = useState({
    salary: '',
    years: '',
    months: '',
    result: null
  });

  // Tax Slabs for FY 2025-26 (New Regime)
  const newRegimeSlabs = [
    { limit: 400000, rate: 0 },     // Up to 4L - Nil
    { limit: 800000, rate: 5 },     // 4L to 8L - 5%
    { limit: 1200000, rate: 10 },   // 8L to 12L - 10%
    { limit: 1600000, rate: 15 },   // 12L to 16L - 15%
    { limit: 2000000, rate: 20 },   // 16L to 20L - 20%
    { limit: 2400000, rate: 25 },   // 20L to 24L - 25%
    { limit: Infinity, rate: 30 }   // Above 24L - 30%
  ];

  // Standard deduction amount (New Regime)
  const STANDARD_DEDUCTION = 75000;

  // Old Regime: standard deduction and slabs by age group
  const OLD_REGIME_STANDARD_DEDUCTION = 50000;
  const oldRegimeSlabsByAge = {
    below60: [
      { limit: 250000, rate: 0 },     // Up to 2.5L - Nil
      { limit: 500000, rate: 5 },     // 2.5L to 5L - 5%
      { limit: 1000000, rate: 20 },   // 5L to 10L - 20%
      { limit: Infinity, rate: 30 }   // Above 10L - 30%
    ],
    senior: [
      { limit: 300000, rate: 0 },     // Up to 3L - Nil (60-80 yrs)
      { limit: 500000, rate: 5 },
      { limit: 1000000, rate: 20 },
      { limit: Infinity, rate: 30 }
    ],
    superSenior: [
      { limit: 500000, rate: 0 },     // Up to 5L - Nil (80+ yrs)
      { limit: 1000000, rate: 20 },
      { limit: Infinity, rate: 30 }
    ]
  };

  // Old Regime deduction caps
  const OLD_REGIME_CAPS = {
    sec80C: 150000,
    sec80CCD1B: 50000,
    homeLoanInterest: 200000
  };

  // Compute slab-wise tax for a given taxable income and slab table
  const computeSlabTax = (taxableIncome, slabs) => {
    let tax = 0;
    let remainingIncome = taxableIncome;
    let previousLimit = 0;
    const slabwiseTax = [];

    for (const slab of slabs) {
      const slabIncome = Math.min(Math.max(remainingIncome, 0), slab.limit - previousLimit);
      const slabTax = (slabIncome * slab.rate) / 100;
      tax += slabTax;

      if (slabIncome > 0) {
        slabwiseTax.push({ income: slabIncome, rate: slab.rate, tax: slabTax });
      }

      remainingIncome -= slabIncome;
      previousLimit = slab.limit;
      if (remainingIncome <= 0) break;
    }

    return { tax, slabwiseTax };
  };

  // New Regime calculation (FY 2025-26)
  const computeNewRegimeTax = (inputs) => {
    const { salary, otherIncome, deductions } = inputs;
    const standardDeduction = Math.min(salary, STANDARD_DEDUCTION);

    // Gross income = salary + other income - allowed deductions
    const grossIncome = Math.max(salary + otherIncome - deductions, 0);
    const taxableIncome = Math.max(grossIncome - standardDeduction, 0);

    const { tax, slabwiseTax } = computeSlabTax(taxableIncome, newRegimeSlabs);

    // Rebate u/s 87A + marginal relief:
    // margin-amount = Income Tax (before relief) - (Gross Income - 1275000)
    const marginAmount = tax - (grossIncome - 1275000);
    const marginalRelief = marginAmount > 0 ? marginAmount : 0;
    const taxAfterRelief = Math.max(tax - marginalRelief, 0);

    const cess = (taxAfterRelief * 4) / 100;
    const totalTax = taxAfterRelief + cess;
    const totalIncome = salary + otherIncome;

    return {
      regime: 'new',
      grossIncome,
      standardDeduction,
      otherDeductions: deductions,
      totalDeductions: standardDeduction + deductions,
      taxableIncome,
      tax: taxAfterRelief,
      taxBeforeRelief: Math.max(tax, 0),
      rebate87A: 0,
      marginalRelief,
      marginAmount,
      cess,
      totalTax,
      effectiveRate: taxableIncome > 0 ? (totalTax / taxableIncome) * 100 : 0,
      monthlyTax: totalTax / 12,
      takeHome: totalIncome - totalTax,
      slabwiseTax
    };
  };

  // Old Regime calculation (FY 2025-26)
  const computeOldRegimeTax = (inputs) => {
    const {
      salary, otherIncome, ageGroup, hra, sec80C, sec80D,
      sec80CCD1B, homeLoanInterest, otherDeductions
    } = inputs;

    const grossIncome = salary + otherIncome;
    const standardDeduction = Math.min(salary, OLD_REGIME_STANDARD_DEDUCTION);

    const deductionBreakup = {
      hra: Math.min(hra, salary),
      sec80C: Math.min(sec80C, OLD_REGIME_CAPS.sec80C),
      sec80D,
      sec80CCD1B: Math.min(sec80CCD1B, OLD_REGIME_CAPS.sec80CCD1B),
      homeLoanInterest: Math.min(homeLoanInterest, OLD_REGIME_CAPS.homeLoanInterest),
      otherDeductions
    };
    const otherDeductionsTotal = Object.values(deductionBreakup).reduce((sum, v) => sum + v, 0);

    const taxableIncome = Math.max(grossIncome - standardDeduction - otherDeductionsTotal, 0);

    const slabs = oldRegimeSlabsByAge[ageGroup] || oldRegimeSlabsByAge.below60;
    const { tax, slabwiseTax } = computeSlabTax(taxableIncome, slabs);

    // Rebate u/s 87A: up to ₹12,500 if taxable income ≤ ₹5,00,000
    const rebate87A = taxableIncome <= 500000 ? Math.min(tax, 12500) : 0;
    const taxAfterRebate = Math.max(tax - rebate87A, 0);

    const cess = (taxAfterRebate * 4) / 100;
    const totalTax = taxAfterRebate + cess;

    return {
      regime: 'old',
      grossIncome,
      standardDeduction,
      otherDeductions: otherDeductionsTotal,
      deductionBreakup,
      totalDeductions: standardDeduction + otherDeductionsTotal,
      taxableIncome,
      tax: taxAfterRebate,
      taxBeforeRelief: Math.max(tax, 0),
      rebate87A,
      marginalRelief: 0,
      marginAmount: 0,
      cess,
      totalTax,
      effectiveRate: taxableIncome > 0 ? (totalTax / taxableIncome) * 100 : 0,
      monthlyTax: totalTax / 12,
      takeHome: grossIncome - totalTax,
      slabwiseTax
    };
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Calculate Simple FD
  const calculateSimpleFD = () => {
    const p = parseFloat(simpleFD.principal);
    const r = parseFloat(simpleFD.rate) / 100;
    const years = parseFloat(simpleFD.years) || 0;
    const months = parseFloat(simpleFD.months) || 0;
    const days = parseFloat(simpleFD.days) || 0;

    // Convert to total years (365 days = 1 year, 30 days = 1 month)
    const t = years + (months / 12) + (days / 365);

    const interest = p * r * t;
    const maturityAmount = p + interest;

    setSimpleFD({
      ...simpleFD,
      result: {
        interest,
        maturityAmount
      }
    });
  };

  // Calculate Compound FD
  const calculateCompoundFD = () => {
    const p = parseFloat(compoundFD.principal);
    const r = parseFloat(compoundFD.rate) / 100;
    const years = parseFloat(compoundFD.years) || 0;
    const months = parseFloat(compoundFD.months) || 0;
    const days = parseFloat(compoundFD.days) || 0;
    const n = compoundFD.frequency;

    // Convert to total years (365 days = 1 year, 30 days = 1 month)
    const t = years + (months / 12) + (days / 365);

    const maturityAmount = p * Math.pow(1 + (r / n), n * t);
    const interest = maturityAmount - p;

    setCompoundFD({
      ...compoundFD,
      result: {
        interest,
        maturityAmount
      }
    });
  };

  // Calculate RD (Quarterly Compounding)
  const calculateRD = () => {
    const p = parseFloat(rd.monthlyInvestment);
    const r = parseFloat(rd.rate); // Annual rate in percent
    const years = parseFloat(rd.years) || 0;
    const months = parseFloat(rd.months) || 0;
    const totalMonths = years * 12 + months; // Total time in months

    // Standard Indian RD formula with quarterly compounding
    // M = P * [ (1 + r/400)^{n} - 1 ] / [1 - (1 + r/400)^{-1/3} ]
    // where n = totalMonths / 3 (number of quarters)
    const quarterlyRate = r / 400;
    const quarters = totalMonths / 3; // number of quarters
    const base = 1 + quarterlyRate;
    const numerator = Math.pow(base, quarters) - 1;
    const denominator = 1 - Math.pow(base, -1 / 3);
    const rdMaturityAmount = p * (numerator / denominator);
    const rdTotalInvestment = p * totalMonths;
    const rdInterest = rdMaturityAmount - rdTotalInvestment;

    setRD({
      ...rd,
      result: {
        totalInvestment: rdTotalInvestment,
        interest: rdInterest,
        maturityAmount: rdMaturityAmount
      }
    });
  };

  // Calculate SIP (Step-up)
  const calculateSIP = () => {
    const baseP = parseFloat(sip.monthlyInvestment);
    const r = parseFloat(sip.rate) / 100 / 12; // Monthly rate
    const years = parseFloat(sip.years) || 0;
    const months = parseFloat(sip.months) || 0;
    const t = years * 12 + months; // Total time in months
    const stepUp = parseFloat(sip.stepUp) || 0; // Yearly appraisal in %

    let maturityAmount = 0;
    let totalInvestment = 0;
    for (let i = 0; i < t; i++) {
      // Calculate which year this month is in
      const year = Math.floor(i / 12);
      // Calculate the SIP for this year
      const p = baseP * Math.pow(1 + stepUp / 100, year);
      // Each installment grows for (t - i) months
      maturityAmount += p * Math.pow(1 + r, t - i - 1);
      totalInvestment += p;
    }
    const interest = maturityAmount - totalInvestment;

    setSIP({
      ...sip,
      result: {
        totalInvestment,
        interest,
        maturityAmount
      }
    });
  };

  // Calculate Monthly Payout
  const calculateMonthlyPayout = () => {
    const p = parseFloat(monthlyPayout.principal);
    const r = parseFloat(monthlyPayout.rate) / 100 / 12; // Monthly rate
    const t = parseFloat(monthlyPayout.time) * 12; // Time in months

    const monthlyAmount = (p * r * Math.pow(1 + r, t)) / (Math.pow(1 + r, t) - 1);
    const totalPayout = monthlyAmount * t;
    const totalInterest = totalPayout - p;

    setMonthlyPayout({
      ...monthlyPayout,
      result: {
        monthlyAmount,
        totalPayout,
        totalInterest
      }
    });
  };

  // Calculate Monthly Interest
  const calculateMonthlyInterest = () => {
    const p = parseFloat(monthlyInterest.principal);
    const r = parseFloat(monthlyInterest.rate) / 100 / 12; // Monthly rate
    const t = parseFloat(monthlyInterest.time) * 12; // Time in months

    const monthlyInterestAmount = p * r;
    const totalInterest = monthlyInterestAmount * t;
    const totalAmount = p + totalInterest;

    setMonthlyInterest({
      ...monthlyInterest,
      result: {
        monthlyInterestAmount,
        totalInterest,
        totalAmount
      }
    });
  };

  // Calculate SWP
  const calculateSWP = () => {
    const p = parseFloat(swp.principal);
    if (!p || p <= 0) {
      alert('Please enter a valid Principal Amount greater than 0');
      return;
    }

    const r = parseFloat(swp.rate) / 100 / 12; // Monthly rate
    if (!r || r <= 0) {
      alert('Please enter a valid Interest Rate greater than 0');
      return;
    }

    const t = parseFloat(swp.time) * 12; // Time in months
    if (!t || t <= 0) {
      alert('Please enter a valid Time Period greater than 0');
      return;
    }

    // If increment is empty or not provided, treat as 0
    const increment = swp.increment === '' || swp.increment === null || swp.increment === undefined
      ? 0
      : parseFloat(swp.increment) / 100; // Annual increment rate

    if (increment < 0) {
      alert('Withdrawal Increment cannot be negative');
      return;
    }

    // Calculate initial monthly withdrawal using the growing annuity formula
    const g = Math.pow(1 + increment, 1 / 12) - 1; // Convert annual increment to monthly
    const initialMonthlyAmount = (p * (r - g)) / (1 - Math.pow(1 + g, t) / Math.pow(1 + r, t));

    let balance = p;
    let totalWithdrawal = 0;
    const monthlyData = [];
    const yearlyData = [];
    let currentYear = 1;
    let yearlyTotal = 0;
    let yearStartBalance = p;

    for (let i = 1; i <= t; i++) {
      const interest = balance * r;
      const currentMonthlyAmount = initialMonthlyAmount * Math.pow(1 + g, i - 1);
      balance = balance + interest - currentMonthlyAmount;
      totalWithdrawal += currentMonthlyAmount;
      yearlyTotal += currentMonthlyAmount;

      monthlyData.push({
        month: i,
        interest,
        withdrawal: currentMonthlyAmount,
        balance
      });

      // Store yearly data
      if (i % 12 === 0 || i === t) {
        const yearEndBalance = balance;
        yearlyData.push({
          year: currentYear,
          monthlyAmount: initialMonthlyAmount * Math.pow(1 + g, (currentYear - 1) * 12), // Show the first month's withdrawal for the year
          yearlyTotal: yearlyTotal,
          corpusBalance: yearEndBalance,
          yearStartBalance: yearStartBalance
        });
        currentYear++;
        yearlyTotal = 0;
        yearStartBalance = balance;
      }
    }

    // Adjust final balance to be exactly zero if it's very close
    if (Math.abs(balance) < 1) {
      balance = 0;
    }

    setSWP({
      ...swp,
      result: {
        initialMonthlyAmount,
        totalWithdrawal,
        finalBalance: balance,
        monthlyData
      },
      yearlyData
    });
  };

  // Calculate EMI
  const calculateEMI = () => {
    const p = parseFloat(emi.principal);
    const r = parseFloat(emi.rate) / 100 / 12; // Monthly rate
    const years = parseFloat(emi.years) || 0;
    const months = parseFloat(emi.months) || 0;
    const t = years * 12 + months; // Total time in months

    const emiAmount = (p * r * Math.pow(1 + r, t)) / (Math.pow(1 + r, t) - 1);
    const totalPayment = emiAmount * t;
    const totalInterest = totalPayment - p;

    // Calculate amortization schedule
    let balance = p;
    const schedule = [];

    for (let i = 1; i <= t; i++) {
      const interest = balance * r;
      const principal = emiAmount - interest;
      balance -= principal;

      schedule.push({
        month: i,
        emi: emiAmount,
        principal,
        interest,
        balance
      });
    }

    setEMI({
      ...emi,
      result: {
        emiAmount,
        totalPayment,
        totalInterest
      },
      breakup: schedule
    });

    // Reset pagination to first year
    setEmiCurrentYear(1);
  };

  // Helper function to save income tax inputs to the database
  const saveIncomeTaxInputs = async (salary, otherIncome, deductions) => {
    try {
      const { data, error } = await supabase
        .from('income_tax_calculations')
        .insert([
          {
            salary: salary,
            other_income: otherIncome,
            deductions: deductions,
            created_at: new Date().toISOString()
          }
        ]);

      if (error) {
        console.error('Error saving income tax inputs:', error);
        return;
      }

      console.log('Income tax inputs saved successfully:', data);
    } catch (error) {
      console.error('Failed to save income tax inputs:', error);
    }
  };

  // Calculate Income Tax
  const calculateIncomeTax = async () => {
    const salary = parseFloat(incomeTax.salary) || 0;
    const otherIncome = parseFloat(incomeTax.otherIncome) || 0;
    const deductions = parseFloat(incomeTax.deductions) || 0;
    const num = (v) => Math.max(parseFloat(v) || 0, 0);

    const inputs = {
      salary,
      otherIncome,
      deductions,
      ageGroup: incomeTax.ageGroup,
      hra: num(incomeTax.hra),
      sec80C: num(incomeTax.sec80C),
      sec80D: num(incomeTax.sec80D),
      sec80CCD1B: num(incomeTax.sec80CCD1B),
      homeLoanInterest: num(incomeTax.homeLoanInterest),
      otherDeductions: num(incomeTax.otherDeductions)
    };

    // Compute both regimes so the user can compare
    const newResult = computeNewRegimeTax(inputs);
    const oldResult = computeOldRegimeTax(inputs);
    const result = incomeTax.regime === 'old' ? oldResult : newResult;

    // Save to database (deductions as per selected regime)
    saveIncomeTaxInputs(salary, otherIncome, result.otherDeductions);

    setIncomeTax({
      ...incomeTax,
      result,
      comparison: {
        newTax: newResult.totalTax,
        oldTax: oldResult.totalTax,
        better: newResult.totalTax <= oldResult.totalTax ? 'new' : 'old',
        savings: Math.abs(newResult.totalTax - oldResult.totalTax)
      }
    });
  };

  // Calculate ITR-2
  const calculateITR2 = async () => {
    const salary = parseFloat(itr2.salary) || 0;
    const otherIncome = parseFloat(itr2.otherIncome) || 0;
    const deductions = parseFloat(itr2.deductions) || 0;
    const shortTermGains = parseFloat(itr2.shortTermGains) || 0;
    const longTermGains = parseFloat(itr2.longTermGains) || 0;

    // Save to database (same as income tax)
    saveIncomeTaxInputs(salary, otherIncome, deductions);

    // Add standard deduction for salary income
    const standardDeduction = salary > 0 ? STANDARD_DEDUCTION : 0;

    // Calculate gross income as (salary + other income - deductions)
    const grossIncome = Math.max(salary + otherIncome - deductions, 0);

    // Calculate taxable income after standard deduction
    const taxableIncome = Math.max(grossIncome - standardDeduction, 0);

    // Total income including capital gains
    const totalIncomeWithGains = grossIncome + shortTermGains + longTermGains;
    const totalTaxableIncome = taxableIncome + shortTermGains + longTermGains;

    let tax = 0;
    let shortTermGainsTax = 0;
    let longTermGainsTax = 0;
    let remainingIncome = taxableIncome;
    let previousLimit = 0;
    let slabwiseTax = [];

    // Calculate tax on regular income (same as Income Tax Calculator)
    for (const slab of newRegimeSlabs) {
      const slabIncome = Math.min(Math.max(remainingIncome, 0), slab.limit - previousLimit);
      const slabTax = (slabIncome * slab.rate) / 100;
      tax += slabTax;

      if (slabIncome > 0) {
        slabwiseTax.push({
          income: slabIncome,
          rate: slab.rate,
          tax: slabTax
        });
      }

      remainingIncome -= slabIncome;
      previousLimit = slab.limit;
      if (remainingIncome <= 0) break;
    }

    // Calculate tax on capital gains
    shortTermGainsTax = Math.max(shortTermGains * 0.20, 0);
    if (longTermGains > 125000) {
      longTermGainsTax = Math.max((longTermGains - 125000) * 0.125, 0);
    }

    const totalCapitalGainsTax = shortTermGainsTax + longTermGainsTax;

    // Calculate marginal relief for regular income tax only
    // margin-amount = Regular Income Tax - (Gross Income - 1275000)
    const marginAmount = tax - (grossIncome - 1275000);

    // Apply marginal relief only if margin amount is positive
    const marginalRelief = marginAmount > 0 ? marginAmount : 0;
    const regularTaxAfterRelief = Math.max(tax - marginalRelief, 0);

    // Total tax after relief = Regular tax after relief + Capital gains tax
    const totalTaxAfterRelief = regularTaxAfterRelief + totalCapitalGainsTax;

    // Calculate cess (4% of total tax after relief)
    const cess = (totalTaxAfterRelief * 4) / 100;
    const totalTax = totalTaxAfterRelief + cess;

    setITR2({
      ...itr2,
      result: {
        grossIncome: totalIncomeWithGains,
        standardDeduction,
        otherDeductions: deductions,
        taxableIncome: totalTaxableIncome,
        tax: Math.max(totalTaxAfterRelief, 0),
        regularTaxAfterRelief: Math.max(regularTaxAfterRelief, 0),
        taxBeforeRelief: Math.max(tax, 0),
        marginalRelief,
        marginAmount,
        shortTermGains,
        longTermGains,
        shortTermGainsTax,
        longTermGainsTax,
        totalCapitalGainsTax,
        cess,
        totalTax,
        effectiveRate: totalTaxableIncome > 0 ? (totalTax / totalIncomeWithGains) * 100 : 0,
        monthlyTax: totalTax / 12,
        takeHome: totalIncomeWithGains - totalTax,
        slabwiseTax
      }
    });
  };

  // Calculate Gratuity
  const calculateGratuity = () => {
    const salary = parseFloat(gratuity.salary) || 0;
    const years = parseFloat(gratuity.years) || 0;
    const months = parseFloat(gratuity.months) || 0;
    const totalService = years + (months / 12);
    if (totalService < 5) {
      setGratuity({
        ...gratuity,
        result: {
          notEligible: true
        }
      });
      return;
    }
    // Standard Indian formula
    const gratuityAmount = (salary * 15 * totalService) / 26;
    setGratuity({
      ...gratuity,
      result: {
        gratuityAmount,
        notEligible: false
      }
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={isMobile ? 0 : 3}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          bgcolor: isMobile ? 'transparent' : 'background.paper'
        }}
      >
        {isMobile ? (
          showMobileMenu ? (
            /* Mobile: icon-block menu */
            <Box>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <CalculateIcon sx={{ mr: 1 }} />
                Finance Calculator
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose a calculator
              </Typography>
              <Grid container spacing={1.5}>
                {CALCULATORS.map((calc, index) => {
                  const Icon = calc.icon;
                  const selected = index === activeTab;
                  return (
                    <Grid item xs={4} key={calc.label}>
                      <Card
                        elevation={0}
                        onClick={() => openCalculator(index)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') openCalculator(index);
                        }}
                        sx={{
                          height: '100%',
                          borderRadius: 3,
                          cursor: 'pointer',
                          border: '1px solid',
                          borderColor: selected ? calc.color : 'divider',
                          bgcolor: 'background.paper',
                          transition: 'transform 0.15s',
                          '&:active': { transform: 'scale(0.96)' }
                        }}
                      >
                        <Box
                          sx={{
                            p: 1.5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: 1
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 44,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: alpha(calc.color, 0.12),
                              color: calc.color
                            }}
                          >
                            <Icon />
                          </Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                            {calc.label}
                          </Typography>
                        </Box>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ) : (
            /* Mobile: calculator header with back button */
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 1,
                mx: -1.5,
                px: 1.5,
                bgcolor: 'background.default',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <IconButton edge="start" onClick={() => setShowMobileMenu(true)} aria-label="All calculators">
                <ArrowBackIcon />
              </IconButton>
              {(() => {
                const calc = CALCULATORS[activeTab];
                const Icon = calc.icon;
                return (
                  <>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(calc.color, 0.12),
                        color: calc.color,
                        flexShrink: 0
                      }}
                    >
                      <Icon fontSize="small" />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }} noWrap>
                        {calc.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {calc.description}
                      </Typography>
                    </Box>
                  </>
                );
              })()}
              <IconButton onClick={() => setShowMobileMenu(true)} aria-label="Show calculator menu">
                <AppsIcon />
              </IconButton>
            </Box>
          )
        ) : (
          <>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CalculateIcon sx={{ mr: 1 }} />
              Finance Calculator
            </Typography>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
            >
              {CALCULATORS.map((calc) => {
                const Icon = calc.icon;
                return <Tab key={calc.label} label={calc.label} icon={<Icon />} />;
              })}
            </Tabs>
          </>
        )}

        {/* On mobile, hide calculators while the menu is open */}
        <Box sx={{ display: isMobile && showMobileMenu ? 'none' : 'block' }}>

        {/* Simple FD Calculator */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    Simple FD Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Principal Amount"
                        type="number"
                        value={simpleFD.principal}
                        onChange={(e) => setSimpleFD({ ...simpleFD, principal: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate"
                        type="number"
                        value={simpleFD.rate}
                        onChange={(e) => setSimpleFD({ ...simpleFD, rate: e.target.value })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Years"
                        type="number"
                        value={simpleFD.years}
                        onChange={(e) => setSimpleFD({ ...simpleFD, years: e.target.value })}
                        size="small"
                        helperText="Years"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Months"
                        type="number"
                        value={simpleFD.months}
                        onChange={(e) => setSimpleFD({ ...simpleFD, months: e.target.value })}
                        size="small"
                        helperText="0-11"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Days"
                        type="number"
                        value={simpleFD.days}
                        onChange={(e) => setSimpleFD({ ...simpleFD, days: e.target.value })}
                        size="small"
                        helperText="0-30"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateSimpleFD}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            {simpleFD.result && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Results</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          Maturity Amount: {formatCurrency(simpleFD.result.maturityAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Principal Amount: {formatCurrency(simpleFD.principal)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Interest Earned: {formatCurrency(simpleFD.result.interest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Principal', value: parseFloat(simpleFD.principal) },
                                { name: 'Interest', value: simpleFD.result.interest }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Compound FD Calculator */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    Compound FD Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Principal Amount"
                        type="number"
                        value={compoundFD.principal}
                        onChange={(e) => setCompoundFD({ ...compoundFD, principal: e.target.value })}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate"
                        type="number"
                        value={compoundFD.rate}
                        onChange={(e) => setCompoundFD({ ...compoundFD, rate: e.target.value })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        select
                        label="Compounding Frequency"
                        value={compoundFD.frequency}
                        onChange={(e) => setCompoundFD({ ...compoundFD, frequency: e.target.value })}
                        size="small"
                      >
                        <MenuItem value={1}>Yearly</MenuItem>
                        <MenuItem value={2}>Half-yearly</MenuItem>
                        <MenuItem value={4}>Quarterly</MenuItem>
                        <MenuItem value={12}>Monthly</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Years"
                        type="number"
                        value={compoundFD.years}
                        onChange={(e) => setCompoundFD({ ...compoundFD, years: e.target.value })}
                        size="small"
                        helperText="Years"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Months"
                        type="number"
                        value={compoundFD.months}
                        onChange={(e) => setCompoundFD({ ...compoundFD, months: e.target.value })}
                        size="small"
                        helperText="0-11"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Days"
                        type="number"
                        value={compoundFD.days}
                        onChange={(e) => setCompoundFD({ ...compoundFD, days: e.target.value })}
                        size="small"
                        helperText="0-30"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateCompoundFD}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            {compoundFD.result && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Results</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          Maturity Amount: {formatCurrency(compoundFD.result.maturityAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Principal Amount: {formatCurrency(compoundFD.principal)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Interest Earned: {formatCurrency(compoundFD.result.interest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Principal', value: parseFloat(compoundFD.principal) },
                                { name: 'Interest', value: compoundFD.result.interest }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* RD Calculator */}
        <TabPanel value={activeTab} index={2} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    Recurring Deposit Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Monthly Investment"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={rd.monthlyInvestment}
                        onChange={(e) => setRD({ ...rd, monthlyInvestment: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate (% p.a.)"
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        value={rd.rate}
                        onChange={(e) => setRD({ ...rd, rate: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Years"
                        type="number"
                        value={rd.years}
                        onChange={(e) => setRD({ ...rd, years: e.target.value })}
                        size="small"
                        helperText="Years"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Months"
                        type="number"
                        value={rd.months}
                        onChange={(e) => setRD({ ...rd, months: e.target.value })}
                        size="small"
                        helperText="0-11"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateRD}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {rd.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          Maturity Amount: {formatCurrency(rd.result.maturityAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Invested Amount: {formatCurrency(rd.result.totalInvestment)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Interest Earned: {formatCurrency(rd.result.interest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Investment', value: rd.result.totalInvestment },
                                { name: 'Interest', value: rd.result.interest }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* SIP Calculator */}
        <TabPanel value={activeTab} index={3} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    Systematic Investment Plan Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Monthly Investment"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={sip.monthlyInvestment}
                        onChange={(e) => setSIP({ ...sip, monthlyInvestment: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate (% p.a.)"
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        value={sip.rate}
                        onChange={(e) => setSIP({ ...sip, rate: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Years"
                        type="number"
                        value={sip.years}
                        onChange={(e) => setSIP({ ...sip, years: e.target.value })}
                        size="small"
                        helperText="Years"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Months"
                        type="number"
                        value={sip.months}
                        onChange={(e) => setSIP({ ...sip, months: e.target.value })}
                        size="small"
                        helperText="0-11"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Step Up (% p.a.)"
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        value={sip.stepUp}
                        onChange={(e) => setSIP({ ...sip, stepUp: e.target.value })}
                        size="small"
                        helperText="Yearly increase in investment amount"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateSIP}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {sip.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          Maturity Amount: {formatCurrency(sip.result.maturityAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Total Investment: {formatCurrency(sip.result.totalInvestment)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Interest Earned: {formatCurrency(sip.result.interest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Investment', value: sip.result.totalInvestment },
                                { name: 'Interest', value: sip.result.interest }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Monthly Payout Calculator */}
        <TabPanel value={activeTab} index={4} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    Monthly Payout Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Principal Amount"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={monthlyPayout.principal}
                        onChange={(e) => setMonthlyPayout({ ...monthlyPayout, principal: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate (% p.a.)"
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        value={monthlyPayout.rate}
                        onChange={(e) => setMonthlyPayout({ ...monthlyPayout, rate: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Time Period (Years)"
                        type="number"
                        value={monthlyPayout.time}
                        onChange={(e) => setMonthlyPayout({ ...monthlyPayout, time: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateMonthlyPayout}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {monthlyPayout.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          Monthly Payout: {formatCurrency(monthlyPayout.result.monthlyAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Total Interest: {formatCurrency(monthlyPayout.result.totalInterest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Total Payout: {formatCurrency(monthlyPayout.result.totalPayout)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Principal', value: parseFloat(monthlyPayout.principal) },
                                { name: 'Interest', value: monthlyPayout.result.totalInterest }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Monthly Interest Calculator */}
        <TabPanel value={activeTab} index={5} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    Monthly Interest Payout Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Principal Amount"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={monthlyInterest.principal}
                        onChange={(e) => setMonthlyInterest({ ...monthlyInterest, principal: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate (% p.a.)"
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        value={monthlyInterest.rate}
                        onChange={(e) => setMonthlyInterest({ ...monthlyInterest, rate: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Time Period (Years)"
                        type="number"
                        value={monthlyInterest.time}
                        onChange={(e) => setMonthlyInterest({ ...monthlyInterest, time: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateMonthlyInterest}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {monthlyInterest.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          Monthly Interest Payout: {formatCurrency(monthlyInterest.result.monthlyInterestAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Principal Amount: {formatCurrency(monthlyInterest.principal)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Total Interest Payout: {formatCurrency(monthlyInterest.result.totalInterest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Total Amount: {formatCurrency(monthlyInterest.result.totalAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Principal', value: parseFloat(monthlyInterest.principal) },
                                { name: 'Total Interest', value: monthlyInterest.result.totalInterest }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* SWP Calculator */}
        <TabPanel value={activeTab} index={6} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    Systematic Withdrawal Plan Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Principal Amount"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={swp.principal}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value === '' || (parseFloat(value) >= 0)) {
                            setSWP({ ...swp, principal: value });
                          }
                        }}
                        size="small"
                        required
                        error={swp.principal !== '' && parseFloat(swp.principal) <= 0}
                        helperText={swp.principal !== '' && parseFloat(swp.principal) <= 0 ? 'Principal Amount must be greater than 0' : ''}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate (% p.a.)"
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        value={swp.rate}
                        onChange={(e) => setSWP({ ...swp, rate: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Time Period (Years)"
                        type="number"
                        value={swp.time}
                        onChange={(e) => setSWP({ ...swp, time: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Withdrawal Increment (% p.a.)"
                        type="number"
                        placeholder="0"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        value={swp.increment}
                        onChange={(e) => setSWP({ ...swp, increment: e.target.value })}
                        size="small"
                        helperText="Annual increase in withdrawal amount (leave empty for 0%)"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateSWP}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {swp.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          Initial Monthly Withdrawal: {formatCurrency(swp.result.initialMonthlyAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Total Withdrawal: {formatCurrency(swp.result.totalWithdrawal)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Final Balance: {formatCurrency(swp.result.finalBalance)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Principal', value: parseFloat(swp.principal) },
                                { name: 'Total Withdrawal', value: swp.result.totalWithdrawal }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                          Yearly Withdrawal Schedule
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Year</TableCell>
                                <TableCell align="right">Monthly Withdrawal</TableCell>
                                <TableCell align="right">Yearly Total</TableCell>
                                <TableCell align="right">Corpus Balance</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {swp.yearlyData && swp.yearlyData.map((yearData) => (
                                <TableRow key={yearData.year}>
                                  <TableCell>Year {yearData.year}</TableCell>
                                  <TableCell align="right">{formatCurrency(yearData.monthlyAmount)}</TableCell>
                                  <TableCell align="right">{formatCurrency(yearData.yearlyTotal)}</TableCell>
                                  <TableCell align="right">{formatCurrency(yearData.corpusBalance)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* EMI Calculator */}
        <TabPanel value={activeTab} index={7} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    EMI Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Principal Amount"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={emi.principal}
                        onChange={(e) => setEMI({ ...emi, principal: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Interest Rate (% p.a.)"
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        value={emi.rate}
                        onChange={(e) => setEMI({ ...emi, rate: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Years"
                        type="number"
                        value={emi.years}
                        onChange={(e) => setEMI({ ...emi, years: e.target.value })}
                        size="small"
                        helperText="Years"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Months"
                        type="number"
                        value={emi.months}
                        onChange={(e) => setEMI({ ...emi, months: e.target.value })}
                        size="small"
                        helperText="0-11"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateEMI}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {emi.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          EMI Amount: {formatCurrency(emi.result.emiAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Total Payment: {formatCurrency(emi.result.totalPayment)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Total Interest: {formatCurrency(emi.result.totalInterest)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Principal', value: parseFloat(emi.principal) },
                                { name: 'Total Interest', value: emi.result.totalInterest }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>

            {/* Amortization Schedule */}
            {emi.result && emi.breakup && (() => {
              const totalYears = Math.ceil(emi.breakup.length / 12);
              const startMonth = (emiCurrentYear - 1) * 12;
              const endMonth = Math.min(emiCurrentYear * 12, emi.breakup.length);
              const currentYearData = emi.breakup.slice(startMonth, endMonth);

              // Calculate year totals
              const yearTotalPrincipal = currentYearData.reduce((sum, row) => sum + row.principal, 0);
              const yearTotalInterest = currentYearData.reduce((sum, row) => sum + row.interest, 0);
              const yearTotalEMI = currentYearData.reduce((sum, row) => sum + row.emi, 0);

              return (
                <Grid item xs={12}>
                  <Card elevation={2} sx={{ borderRadius: 2, mt: 2 }}>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                      {/* Header Section */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                        pb: 2,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Amortization Schedule - Year {emiCurrentYear}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Tenure: {emi.breakup.length} months ({totalYears} years)
                        </Typography>
                      </Box>

                      <TableContainer sx={{ maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{
                                backgroundColor: 'grey.50',
                                fontWeight: 600
                              }}>
                                Month
                              </TableCell>
                              <TableCell align="right" sx={{
                                backgroundColor: 'grey.50',
                                fontWeight: 600
                              }}>
                                EMI
                              </TableCell>
                              <TableCell align="right" sx={{
                                backgroundColor: 'grey.50',
                                fontWeight: 600
                              }}>
                                Principal
                              </TableCell>
                              <TableCell align="right" sx={{
                                backgroundColor: 'grey.50',
                                fontWeight: 600
                              }}>
                                Interest
                              </TableCell>
                              <TableCell align="right" sx={{
                                backgroundColor: 'grey.50',
                                fontWeight: 600
                              }}>
                                Balance
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {currentYearData.map((row, index) => (
                              <TableRow
                                key={row.month}
                                sx={{
                                  '&:nth-of-type(odd)': {
                                    backgroundColor: 'action.hover'
                                  },
                                  '&:hover': {
                                    backgroundColor: 'action.selected'
                                  }
                                }}
                              >
                                <TableCell sx={{ fontWeight: 500 }}>{row.month}</TableCell>
                                <TableCell align="right">{formatCurrency(row.emi)}</TableCell>
                                <TableCell align="right" sx={{ color: 'success.main' }}>
                                  {formatCurrency(row.principal)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'warning.main' }}>
                                  {formatCurrency(row.interest)}
                                </TableCell>
                                <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                  {formatCurrency(Math.max(row.balance, 0))}
                                </TableCell>
                              </TableRow>
                            ))}
                            {/* Year Total Row */}
                            <TableRow sx={{
                              backgroundColor: 'grey.100',
                              '& .MuiTableCell-root': {
                                fontWeight: 700,
                                borderTop: '2px solid',
                                borderColor: 'divider'
                              }
                            }}>
                              <TableCell>Year {emiCurrentYear} Total</TableCell>
                              <TableCell align="right">{formatCurrency(yearTotalEMI)}</TableCell>
                              <TableCell align="right" sx={{ color: 'success.main' }}>
                                {formatCurrency(yearTotalPrincipal)}
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'warning.main' }}>
                                {formatCurrency(yearTotalInterest)}
                              </TableCell>
                              <TableCell align="right">-</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      {/* Pagination */}
                      {totalYears > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Pagination
                            count={totalYears}
                            page={emiCurrentYear}
                            onChange={(event, value) => setEmiCurrentYear(value)}
                            color="primary"
                            size="small"
                            showFirstButton
                            showLastButton
                          />
                        </Box>
                      )}

                      {/* Summary for current year */}
                      <Box sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider'
                      }}>
                        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                          Year {emiCurrentYear} Summary
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2">
                              <strong>Total EMI:</strong> {formatCurrency(yearTotalEMI)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2">
                              <strong>Principal Paid:</strong> <span style={{ color: '#2e7d32' }}>{formatCurrency(yearTotalPrincipal)}</span>
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography variant="body2">
                              <strong>Interest Paid:</strong> <span style={{ color: '#ed6c02' }}>{formatCurrency(yearTotalInterest)}</span>
                            </Typography>
                          </Grid>
                        </Grid>

                        {/* Simple progress bar */}
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="caption" color="text.secondary" gutterBottom>
                            Principal vs Interest Ratio
                          </Typography>
                          <Box sx={{
                            display: 'flex',
                            height: 6,
                            borderRadius: 3,
                            overflow: 'hidden',
                            backgroundColor: 'grey.200'
                          }}>
                            <Box sx={{
                              width: `${(yearTotalPrincipal / (yearTotalPrincipal + yearTotalInterest)) * 100}%`,
                              backgroundColor: 'success.main'
                            }} />
                            <Box sx={{
                              width: `${(yearTotalInterest / (yearTotalPrincipal + yearTotalInterest)) * 100}%`,
                              backgroundColor: 'warning.main'
                            }} />
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Principal: {((yearTotalPrincipal / (yearTotalPrincipal + yearTotalInterest)) * 100).toFixed(1)}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Interest: {((yearTotalInterest / (yearTotalPrincipal + yearTotalInterest)) * 100).toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })()}
          </Grid>
        </TabPanel>

        {/* Income Tax Calculator */}
        <TabPanel value={activeTab} index={8} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    Income Tax Calculator (FY 2025-26)
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <ToggleButtonGroup
                        value={incomeTax.regime}
                        exclusive
                        fullWidth
                        size="small"
                        color="primary"
                        onChange={(e, value) => {
                          if (value) setIncomeTax({ ...incomeTax, regime: value, result: null, comparison: null });
                        }}
                      >
                        <ToggleButton value="new">New Regime</ToggleButton>
                        <ToggleButton value="old">Old Regime</ToggleButton>
                      </ToggleButtonGroup>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Annual Salary Income"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={incomeTax.salary}
                        onChange={(e) => setIncomeTax({ ...incomeTax, salary: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Other Income"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={incomeTax.otherIncome}
                        onChange={(e) => setIncomeTax({ ...incomeTax, otherIncome: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    {incomeTax.regime === 'new' ? (
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Allowed Deductions"
                          type="number"
                          InputProps={{
                            startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                          }}
                          value={incomeTax.deductions}
                          onChange={(e) => setIncomeTax({ ...incomeTax, deductions: e.target.value })}
                          size="small"
                          helperText="e.g. Employer NPS u/s 80CCD(2). Standard deduction of ₹75,000 is added automatically"
                          sx={numberInputStyle}
                        />
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <TextField
                            select
                            fullWidth
                            label="Age Group"
                            value={incomeTax.ageGroup}
                            onChange={(e) => setIncomeTax({ ...incomeTax, ageGroup: e.target.value })}
                            size="small"
                          >
                            <MenuItem value="below60">Below 60 years</MenuItem>
                            <MenuItem value="senior">Senior Citizen (60–80 years)</MenuItem>
                            <MenuItem value="superSenior">Super Senior Citizen (80+ years)</MenuItem>
                          </TextField>
                        </Grid>
                        {[
                          { key: 'hra', label: 'HRA Exemption', helper: 'Exempt portion of HRA u/s 10(13A)' },
                          { key: 'sec80C', label: 'Section 80C', helper: 'PF, PPF, ELSS, LIC, etc. (max ₹1,50,000)' },
                          { key: 'sec80D', label: 'Section 80D', helper: 'Health insurance premium' },
                          { key: 'sec80CCD1B', label: 'Section 80CCD(1B)', helper: 'Additional NPS contribution (max ₹50,000)' },
                          { key: 'homeLoanInterest', label: 'Home Loan Interest u/s 24(b)', helper: 'Self-occupied property (max ₹2,00,000)' },
                          { key: 'otherDeductions', label: 'Other Deductions', helper: '80E, 80G, 80TTA, etc.' }
                        ].map((field) => (
                          <Grid item xs={12} sm={6} key={field.key}>
                            <TextField
                              fullWidth
                              label={field.label}
                              type="number"
                              InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                              }}
                              value={incomeTax[field.key]}
                              onChange={(e) => setIncomeTax({ ...incomeTax, [field.key]: e.target.value })}
                              size="small"
                              helperText={field.helper}
                              sx={numberInputStyle}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary">
                            Standard deduction of ₹50,000 is added automatically. Limits are applied where applicable.
                          </Typography>
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateIncomeTax}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate Tax
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Tax Slabs Information */}
              <Card elevation={2} sx={{ borderRadius: 2, mt: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom>
                    {incomeTax.regime === 'old' ? 'Old' : 'New'} Tax Regime Slabs (FY 2025-26)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Income Range</TableCell>
                          <TableCell align="right">Tax Rate</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(incomeTax.regime === 'old'
                          ? oldRegimeSlabsByAge[incomeTax.ageGroup] || oldRegimeSlabsByAge.below60
                          : newRegimeSlabs
                        ).map((slab, index, slabs) => {
                          const prevLimit = index > 0 ? slabs[index - 1].limit : 0;
                          const label = index === 0
                            ? `Up to ${formatCurrency(slab.limit)}`
                            : slab.limit === Infinity
                              ? `Above ${formatCurrency(prevLimit)}`
                              : `${formatCurrency(prevLimit + 1)} to ${formatCurrency(slab.limit)}`;
                          return (
                            <TableRow key={index}>
                              <TableCell>{label}</TableCell>
                              <TableCell align="right">{slab.rate === 0 ? 'Nil' : `${slab.rate}%`}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {incomeTax.regime === 'old'
                      ? 'Rebate u/s 87A up to ₹12,500 if taxable income ≤ ₹5,00,000. Health & Education Cess @ 4%.'
                      : 'Rebate u/s 87A: no tax if taxable income ≤ ₹12,00,000 (marginal relief applies above). Health & Education Cess @ 4%.'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {incomeTax.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Tax Calculation Results ({incomeTax.result.regime === 'old' ? 'Old' : 'New'} Regime)
                    </Typography>
                    <Grid container spacing={2}>
                      {incomeTax.comparison && (
                        <Grid item xs={12}>
                          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Regime Comparison
                            </Typography>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">New Regime</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: incomeTax.comparison.better === 'new' ? 600 : 400 }}>
                                  {formatCurrency(incomeTax.comparison.newTax)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Old Regime</Typography>
                                <Typography variant="subtitle1" sx={{ fontWeight: incomeTax.comparison.better === 'old' ? 600 : 400 }}>
                                  {formatCurrency(incomeTax.comparison.oldTax)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" color="success.main">
                                  {incomeTax.comparison.savings > 0
                                    ? `${incomeTax.comparison.better === 'new' ? 'New' : 'Old'} Regime saves you ${formatCurrency(incomeTax.comparison.savings)}`
                                    : 'Both regimes result in the same tax'}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Gross Income: {formatCurrency(incomeTax.result.grossIncome)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Standard Deduction: {formatCurrency(incomeTax.result.standardDeduction)}
                        </Typography>
                      </Grid>
                      {incomeTax.result.regime === 'old' && incomeTax.result.otherDeductions > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            Deductions & Exemptions: {formatCurrency(incomeTax.result.otherDeductions)}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Taxable Income: {formatCurrency(incomeTax.result.taxableIncome)}
                        </Typography>
                      </Grid>
                      {incomeTax.result.taxBeforeRelief > 0 && incomeTax.result.tax === 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" color="success.main">
                            No tax payable - Rebate u/s 87A applicable
                          </Typography>
                        </Grid>
                      )}
                      {incomeTax.result.rebate87A > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" color="success.main">
                            Rebate u/s 87A: {formatCurrency(incomeTax.result.rebate87A)}
                          </Typography>
                        </Grid>
                      )}

                      {incomeTax.result.grossIncome > 1275000 && incomeTax.result.marginalRelief > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" color="success.main">
                            Marginal Relief: {formatCurrency(incomeTax.result.marginalRelief)}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="h6" color="primary">
                          Total Tax (after relief): {formatCurrency(Math.max(incomeTax.result.tax, 0))}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Health & Education Cess (4%): {formatCurrency(incomeTax.result.cess)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
                          Total Tax Liability: {formatCurrency(incomeTax.result.totalTax)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                          Take Home Amount: {formatCurrency(incomeTax.result.takeHome)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                          Monthly Take Home: {formatCurrency(incomeTax.result.takeHome / 12)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Effective Tax Rate: {incomeTax.result.effectiveRate.toFixed(2)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Monthly Tax Payable: {formatCurrency(incomeTax.result.monthlyTax)}
                        </Typography>
                      </Grid>
                      {incomeTax.result?.slabwiseTax?.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            Slab-wise Tax Breakdown:
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Income in Slab</TableCell>
                                  <TableCell align="right">Rate</TableCell>
                                  <TableCell align="right">Tax</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {incomeTax.result.slabwiseTax.map((slab, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{formatCurrency(slab.income)}</TableCell>
                                    <TableCell align="right">{slab.rate}%</TableCell>
                                    <TableCell align="right">{formatCurrency(slab.tax)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* ITR-2 Calculator */}
        <TabPanel value={activeTab} index={9} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                  >
                    ITR-2 Calculator (FY 2025-26)
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Annual Salary Income"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={itr2.salary}
                        onChange={(e) => setITR2({ ...itr2, salary: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Other Income"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={itr2.otherIncome}
                        onChange={(e) => setITR2({ ...itr2, otherIncome: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Short Term Capital Gains"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={itr2.shortTermGains}
                        onChange={(e) => setITR2({ ...itr2, shortTermGains: e.target.value })}
                        size="small"
                        helperText="Taxed at 20%"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Long Term Capital Gains"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={itr2.longTermGains}
                        onChange={(e) => setITR2({ ...itr2, longTermGains: e.target.value })}
                        size="small"
                        helperText="Taxed at 12.5% (above ₹1,25,000)"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Total Deductions"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={itr2.deductions}
                        onChange={(e) => setITR2({ ...itr2, deductions: e.target.value })}
                        size="small"
                        helperText="Standard deduction of ₹75,000 will be added automatically"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateITR2}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate Tax
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              {itr2.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                    >
                      Tax Calculation Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Gross Income: {formatCurrency(itr2.result.grossIncome)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Standard Deduction: {formatCurrency(itr2.result.standardDeduction)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          Taxable Income: {formatCurrency(itr2.result.taxableIncome)}
                        </Typography>
                      </Grid>
                      {itr2.result.shortTermGains > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            Short Term Capital Gains: {formatCurrency(itr2.result.shortTermGains)}
                          </Typography>
                        </Grid>
                      )}
                      {itr2.result.longTermGains > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1">
                            Long Term Capital Gains: {formatCurrency(itr2.result.longTermGains)}
                          </Typography>
                        </Grid>
                      )}

                      {itr2.result.grossIncome > 1275000 && itr2.result.marginalRelief > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" color="success.main">
                            Marginal Relief: {formatCurrency(itr2.result.marginalRelief)}
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" color="primary">
                          <strong>Short Term Capital Gains:</strong> {formatCurrency(itr2.result.shortTermGainsTax)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1" color="primary">
                          <strong>Long Term Capital Gains:</strong> {formatCurrency(itr2.result.longTermGainsTax)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="primary">
                          Total Tax (after relief): {formatCurrency(Math.max(itr2.result.tax, 0))}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Health & Education Cess (4%): {formatCurrency(itr2.result.cess)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
                          Total Tax Liability: {formatCurrency(itr2.result.totalTax)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main" sx={{ fontWeight: 600 }}>
                          Take Home Amount: {formatCurrency(itr2.result.takeHome)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">
                          Effective Tax Rate: {itr2.result.effectiveRate.toFixed(2)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Monthly Tax Payable: {formatCurrency(itr2.result.monthlyTax)}
                        </Typography>
                      </Grid>
                      {itr2.result?.slabwiseTax?.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                            Slab-wise Tax Breakdown:
                          </Typography>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Income in Slab</TableCell>
                                  <TableCell align="right">Rate</TableCell>
                                  <TableCell align="right">Tax</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {itr2.result.slabwiseTax.map((slab, index) => (
                                  <TableRow key={index}>
                                    <TableCell>{formatCurrency(slab.income)}</TableCell>
                                    <TableCell align="right">{slab.rate}%</TableCell>
                                    <TableCell align="right">{formatCurrency(slab.tax)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* Gratuity Calculator */}
        <TabPanel value={activeTab} index={10} sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                  <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    Gratuity Calculator
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Last Drawn Salary (Basic + DA)"
                        type="number"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        }}
                        value={gratuity.salary}
                        onChange={(e) => setGratuity({ ...gratuity, salary: e.target.value })}
                        size="small"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Years of Service"
                        type="number"
                        value={gratuity.years}
                        onChange={(e) => setGratuity({ ...gratuity, years: e.target.value })}
                        size="small"
                        helperText="Years"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Months"
                        type="number"
                        value={gratuity.months}
                        onChange={(e) => setGratuity({ ...gratuity, months: e.target.value })}
                        size="small"
                        helperText="0-11"
                        sx={numberInputStyle}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={calculateGratuity}
                        fullWidth
                        sx={{ mt: 1 }}
                      >
                        Calculate
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              {gratuity.result && gratuity.result.notEligible ? (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography variant="h6" color="error" gutterBottom>
                      Not eligible for gratuity (minimum 5 years of service required).
                    </Typography>
                  </CardContent>
                </Card>
              ) : gratuity.result && (
                <Card elevation={2} sx={{ borderRadius: 2, height: '100%' }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, height: '100%' }}>
                    <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                      Results
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h6" color="success.main">
                          Gratuity Amount: {formatCurrency(gratuity.result.gratuityAmount)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sx={{ height: { xs: 200, sm: 300 } }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={[
                                { name: 'Gratuity', value: gratuity.result.gratuityAmount },
                                { name: 'Not Covered', value: Math.max(0, (parseFloat(gratuity.salary) * (parseFloat(gratuity.years) + (parseFloat(gratuity.months) || 0) / 12)) - gratuity.result.gratuityAmount) }
                              ]}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {COLORS.map((color, index) => (
                                <Cell key={`cell-${index}`} fill={color} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default FinanceCalculator;
